import {Locale} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {DEFAULT_HOST, routeTo} from '@/utils/router'

import {getImageLocale} from './utils/helpers'

export const getClassAssistContent = (locale: Locale) => {
  const ogImageLocale = getImageLocale(locale)

  return {
    headerLabel: 'class-assist.class-assist-header-label',
    metaTitle: 'class-assist.class-assist-meta-title',
    metaDescription: 'class-assist.class-assist-meta-desc',
    industryMetaTitle: 'class-assist.class-assist-industry-meta-title',
    industryMetaDescription: 'class-assist.class-assist-industry-meta-desc',
    sectorMetaDescription: 'class-assist.class-assist-sector-meta-desc',
    ogImage: {
      url: `${DEFAULT_HOST}/images/og-images/class-assist-${ogImageLocale}.png`,
      width: 1200,
      height: 627,
      alt: 'Class assist - OG image',
    },
    header: {
      buttonLinkText: 'class-assist.class-assist-header-main-cta',
      buttonLinkHref: routeTo.verification.form(),
    },
    hero: {
      title: 'class-assist.class-assist-hero-title',
      subtitle: 'class-assist.class-assist-hero-subtitle',
      industryTitle: 'class-assist.class-assist-industry-hero-title',
      industrySubtitle: 'class-assist.class-assist-industry-hero-subtitle',
      imageSrc: '/images/hero/class-assist-checklist.svg',
      imageAlt: 'Trademark classes checklist',
    },
    selection: {
      title: 'class-assist.class-assist-selection-title',
      description: 'class-assist.class-assist-selection-desc',
      industryTitle: 'class-assist.class-assist-industry-label',
      industrySelectPlaceholder: 'class-assist.class-assist-industry-placeholder',
      sectorTitle: 'class-assist.class-assist-sector-label',
      sectorSelectPlaceholder: 'class-assist.class-assist-sector-placeholder',
    },
    classTitle: 'class-assist.reg-gs-class-title',
    allClassesTitle: 'class-assist.class-assist-all-classes-title',
    recommendedClassesTitle: 'class-assist.class-assist-recommended-classes-title',
    includesText: 'class-assist.includes-text',
    learnMoreAboutClassText: 'class-assist.class-assist-learn-more-about-class',
    estimatedPriceTitle: 'class-assist.class-assist-estimated-price-title',
    estimatedPriceDescription: 'class-assist.class-assist-estimated-price-description',
    pricingButtonText: 'class-assist.button-show-price-list',
    pricingButtonLink: routeTo.pricing(),
    relatedArticlesTitle: 'class-assist.class-assist-related-articles-title',
    tiles: {
      title: 'class-assist.class-assist-tiles-title',
      list: [
        {
          imageSrc: '/images/icons/verification.svg',
          imageAlt: 'List of goods and services',
          title: 'class-assist.class-assist-tile-0-title',
          description: 'class-assist.class-assist-tile-0-desc',
        },
        {
          imageSrc: '/images/icons/selection.svg',
          imageAlt: 'Selection',
          title: 'class-assist.class-assist-tile-1-title',
          description: 'class-assist.class-assist-tile-1-desc',
        },
        {
          imageSrc: '/images/icons/registration-support.svg',
          imageAlt: 'Application success',
          title: 'class-assist.class-assist-tile-2-title',
          description: 'class-assist.class-assist-tile-2-desc',
        },
      ],
    },
    popularSectors: {
      title: 'class-assist.class-assist-popular-sectors-title',
      imageSrc: '/images/icons/briefing.svg',
      imageAlt: 'Popular sectors',
      list: [
        {industry: '5', sector: '1'},
        {industry: '5', sector: '4'},
        {industry: '5', sector: '9'},
        {industry: '5', sector: '10'},
        {industry: '4', sector: '1'},
        {industry: '4', sector: '2'},
        {industry: '3', sector: '2'},
        {industry: '3', sector: '1'},
        {industry: '2', sector: '1'},
        {industry: '2', sector: '18'},
        {industry: '2', sector: '2'},
        {industry: '1', sector: '1'},
        {industry: '1', sector: '2'},
        {industry: '1', sector: '3'},
        {industry: '8', sector: '8'},
        {industry: '8', sector: '10'},
        {industry: '6', sector: '14'},
        {industry: '6', sector: '7'},
        {industry: '6', sector: '17'},
        {industry: '6', sector: '15'},
      ],
    },
    getStartedBanner: {
      title: 'class-assist.get-started-banner-title',
      buttonLink: routeTo.verification.form(),
      buttonText: 'class-assist.get-started-banner-button',
    },
  }
}

export enum IpoName {
  euipo = 'EUIPO',
  uspto = 'USPTO',
  cipo = 'CIPO',
  auipo = 'IP Australia',
}

export type IpoDataKey = 'euipo' | 'uspto' | 'cipo' | 'auipo'

type IpoContent = {
  country: Jurisdiction
  logoSrc: string
  logoAlt: string
  manualTitle?: string
  dataKey: IpoDataKey
}

export const IPOs: Record<IpoName, IpoContent> = {
  [IpoName.euipo]: {
    country: Jurisdiction.EU,
    dataKey: 'euipo',
    logoSrc: '/images/ipo/euipo-logo.png',
    logoAlt: 'Logo of EUIPO (European Union Intellectual Property Office)',
    manualTitle: 'class-assist.class-assist-gs-class-euipo-manual-title',
  },
  [IpoName.uspto]: {
    country: Jurisdiction.US,
    dataKey: 'uspto',
    logoSrc: '/images/ipo/uspto-logo.png',
    logoAlt: 'Logo of USPTO (United States Patent and Trademark Office)',
    manualTitle: 'class-assist.class-assist-gs-class-uspto-manual-title',
  },
  [IpoName.cipo]: {
    country: Jurisdiction.CA,
    dataKey: 'cipo',
    logoSrc: '/images/ipo/cipo-logo.png',
    logoAlt: 'Logo of CIPO (Canadian Intellectual Property Office)',
    manualTitle: 'class-assist.class-assist-gs-class-cipo-manual-title',
  },
  [IpoName.auipo]: {
    country: Jurisdiction.AU,
    dataKey: 'auipo',
    logoSrc: '/images/ipo/auipo-logo.png',
    logoAlt: 'Logo of IP Australia',
    manualTitle: 'class-assist.class-assist-gs-class-auipo-manual-title',
  },
}

export const getSpecimenDescriptionByClass = (classCode: string) => {
  switch (classCode) {
    case '9':
      return 'class-assist.class-assist-gs-class-specimens-desc-1'
    case '35':
    case '36':
    case '37':
    case '38':
    case '39':
    case '40':
    case '41':
    case '42':
    case '43':
    case '44':
    case '45':
      return 'class-assist.class-assist-gs-class-specimens-desc-2'
    default:
      return 'class-assist.class-assist-gs-class-specimens-desc-0'
  }
}

export const getClassAssistClassContent = (classCode: string) => ({
  metaTitle: 'class-assist.class-assist-gs-class-meta-title',
  hero: {
    title: 'class-assist.class-assist-gs-class-hero-title',
    subtitle: 'class-assist.class-assist-gs-class-hero-subtitle',
  },
  classTitle: 'class-assist.reg-gs-class-title',
  whatIsIncludedTitle: 'class-assist.class-assist-gs-class-what-is-included-title',
  examplesTitle: 'class-assist.class-assist-gs-class-examples-title',
  recommendedTitle: 'class-assist.class-assist-gs-class-recommended-title',
  associatedTitle: 'class-assist.class-assist-gs-class-associated-title',
  associatedDescription: 'class-assist.class-assist-gs-class-associated-description',
  specimenTitle: 'class-assist.class-assist-gs-class-specimens-title',
  manuals: {
    title: 'class-assist.class-assist-gs-class-manuals-title',
    offices: IPOs,
    visitWebsiteButtonText: 'class-assist.visit-website-text',
  },
  description: `class-assist.class-${classCode}-description`,
})

export const getClassAssistItemContent = () => ({
  metaTitle: 'class-assist.class-assist-gs-item-meta-title',
  metaDescription: 'class-assist.class-assist-gs-item-meta-desc',
  hero: {
    title: 'class-assist.class-assist-gs-item-hero-title',
    subtitle: 'class-assist.class-assist-gs-item-hero-subtitle',
  },
  overviewTitle: 'class-assist.class-assist-gs-item-overview-title',
  examplesTitle: 'class-assist.class-assist-gs-item-examples-title',
  recommendedTitle: 'class-assist.class-assist-gs-item-recommended-title',
  associatedTitle: 'class-assist.class-assist-gs-item-associated-title',
  associatedDescription: 'class-assist.class-assist-gs-item-associated-description',
  ipo: {
    title: 'class-assist.class-assist-gs-item-ipo-differences-title',
    description: 'class-assist.class-assist-gs-item-ipo-differences-description',
    tableHeaderOffice: 'class-assist.class-assist-gs-item-ipo-table-header-office',
    tableHeaderItem: 'class-assist.class-assist-gs-item-ipo-table-header-item',
    offices: IPOs,
  },
})
